/**
 * Catalogue des fonds de carte.
 *
 * Reprend `MapManager.kt:140-151` de l'application Android :
 * huit styles vectoriels sans clé d'API, répartis entre OpenFreeMap
 * et VersaTiles, plus la vue satellite Esri en superposition raster.
 */

const OFM_STYLE_BASE_URL = 'https://tiles.openfreemap.org/styles/'
const VT_STYLE_BASE_URL = 'https://tiles.versatiles.org/assets/styles/'

/** Styles servis par VersaTiles ; tous les autres viennent d'OpenFreeMap. */
const VERSATILES_STYLES = new Set([
  'colorful',
  'eclipse',
  'graybeard',
  'neutrino',
  'shadow',
])

export type MapStyleId =
  | 'liberty'
  | 'bright'
  | 'positron'
  | 'colorful'
  | 'eclipse'
  | 'graybeard'
  | 'neutrino'
  | 'shadow'

export interface MapStyleDescriptor {
  readonly id: MapStyleId
  /** Libellé affiché dans le sélecteur de style. */
  readonly label: string
  readonly provider: 'OpenFreeMap' | 'VersaTiles'
  /**
   * Schéma des tuiles vectorielles. Les deux fournisseurs n'utilisent pas
   * le même : cela change le nom de la source-layer et de la propriété de
   * classe de route quand on construit l'overlay routier du mode satellite.
   */
  readonly schema: 'openmaptiles' | 'shortbread'
  /** Seul Liberty embarque le calque `building-3d`. */
  readonly supports3dBuildings: boolean
}

export const MAP_STYLES: readonly MapStyleDescriptor[] = [
  {
    id: 'liberty',
    label: 'Liberty',
    provider: 'OpenFreeMap',
    schema: 'openmaptiles',
    supports3dBuildings: true,
  },
  {
    id: 'bright',
    label: 'Bright',
    provider: 'OpenFreeMap',
    schema: 'openmaptiles',
    supports3dBuildings: false,
  },
  {
    id: 'positron',
    label: 'Positron',
    provider: 'OpenFreeMap',
    schema: 'openmaptiles',
    supports3dBuildings: false,
  },
  {
    id: 'colorful',
    label: 'Colorful',
    provider: 'VersaTiles',
    schema: 'shortbread',
    supports3dBuildings: false,
  },
  {
    id: 'eclipse',
    label: 'Eclipse',
    provider: 'VersaTiles',
    schema: 'shortbread',
    supports3dBuildings: false,
  },
  {
    id: 'graybeard',
    label: 'Graybeard',
    provider: 'VersaTiles',
    schema: 'shortbread',
    supports3dBuildings: false,
  },
  {
    id: 'neutrino',
    label: 'Neutrino',
    provider: 'VersaTiles',
    schema: 'shortbread',
    supports3dBuildings: false,
  },
  {
    id: 'shadow',
    label: 'Shadow',
    provider: 'VersaTiles',
    schema: 'shortbread',
    supports3dBuildings: false,
  },
] as const

export const DEFAULT_STYLE_ID: MapStyleId = 'liberty'

/** Position initiale quand aucune caméra n'a été mémorisée : France entière. */
export const DEFAULT_CAMERA = {
  center: [2.5, 46.5] as [number, number], // MapLibre attend [lng, lat]
  zoom: 5,
  bearing: 0,
  pitch: 0,
} as const

/** Inclinaison et zoom du mode guidage (`MapManager.kt:187-188`). */
export const GUIDANCE_TILT = 55
export const GUIDANCE_ZOOM = 17

export function isVersaTilesStyle(id: string): boolean {
  return VERSATILES_STYLES.has(id)
}

export function getStyleUrl(id: MapStyleId): string {
  return isVersaTilesStyle(id)
    ? `${VT_STYLE_BASE_URL}${id}/style.json`
    : `${OFM_STYLE_BASE_URL}${id}`
}

export function getStyleDescriptor(id: string): MapStyleDescriptor {
  return (
    MAP_STYLES.find((s) => s.id === id) ??
    MAP_STYLES.find((s) => s.id === DEFAULT_STYLE_ID)!
  )
}

/* -------------------------------------------------------------------------
 * Vue satellite (Esri World Imagery)
 * ---------------------------------------------------------------------- */

export const SATELLITE_SOURCE_ID = 'satellite-source'
export const SATELLITE_LAYER_ID = 'satellite-layer'
export const SATELLITE_ROADS_LAYER_ID = 'satellite-roads-layer'

/**
 * Esri sert ses tuiles en `{z}/{y}/{x}` et non `{z}/{x}/{y}` :
 * inverser les deux derniers segments donne une carte silencieusement fausse.
 */
export const SATELLITE_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

export const SATELLITE_ATTRIBUTION = 'Esri, Maxar, Earthstar Geographics'
export const SATELLITE_TILE_SIZE = 256
export const SATELLITE_MAX_ZOOM = 19

/** Overlay routier du mode satellite (`MapManager.kt:172-174`). */
export const SATELLITE_ROADS_OPACITY = 0.55
export const SATELLITE_ROADS_FADE_START = 15
export const SATELLITE_ROADS_FADE_END = 17

/** Classes de routes affichées, selon le schéma du fond de carte. */
const ROAD_CLASSES_SHORTBREAD = [
  'motorway',
  'trunk',
  'primary',
  'secondary',
  'tertiary',
  'unclassified',
  'residential',
  'living_street',
  'service',
  'pedestrian',
  'track',
  'path',
  'cycleway',
  'footway',
] as const

const ROAD_CLASSES_OPENMAPTILES = [
  'motorway',
  'trunk',
  'primary',
  'secondary',
  'tertiary',
  'minor',
  'service',
  'track',
  'path',
] as const

export interface RoadOverlaySchema {
  readonly sourceLayer: string
  readonly classProperty: string
  readonly classes: readonly string[]
}

export function getRoadOverlaySchema(
  style: MapStyleDescriptor,
): RoadOverlaySchema {
  return style.schema === 'shortbread'
    ? {
        sourceLayer: 'streets',
        classProperty: 'kind',
        classes: ROAD_CLASSES_SHORTBREAD,
      }
    : {
        sourceLayer: 'transportation',
        classProperty: 'class',
        classes: ROAD_CLASSES_OPENMAPTILES,
      }
}
